import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useSession } from "@/services/auth";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import { openPdfForPrint } from "@/lib/open-pdf-for-print";

async function fetchShipmentLabel(
  token: string | null | undefined,
  shipmentId: string
) {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl || !token || !shipmentId)
    throw new Error("Label download unavailable");
  const { data } = await axios.get<Blob>(
    `${apiUrl}/api/v2/shipping/shipments/${encodeURIComponent(shipmentId)}/labels/download`,
    { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
  );
  if (!data.size || !data.type.toLowerCase().startsWith("application/pdf")) {
    throw new Error("Expected a PDF label");
  }
  return data;
}

/** The server selects the booked provider's label; never send auth to a returned label URL. */
export function useDownloadShipmentLabel() {
  const { token } = useSession();
  const { t } = useTranslation("app");
  return useMutation({
    mutationFn: async (shipmentId: string) => {
      const data = await fetchShipmentLabel(token, shipmentId);
      return { data, shipmentId };
    },
    onSuccess: ({ data, shipmentId }) => {
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `label-${shipmentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Let the browser begin reading the blob before releasing it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: () =>
      toast({ title: t("orders.downloadLabelError"), variant: "destructive" }),
  });
}

export function usePrintShipmentLabel() {
  const { token } = useSession();
  const { t } = useTranslation("app");
  const [isPending, setIsPending] = useState(false);

  const print = (shipmentId: string) => {
    if (isPending) return;
    setIsPending(true);
    void openPdfForPrint(() => fetchShipmentLabel(token, shipmentId), {
      loading: t("orders.loadingLabel"),
      failed: t("orders.printLabelError"),
    })
      .catch(() =>
        toast({ title: t("orders.printLabelError"), variant: "destructive" })
      )
      .finally(() => setIsPending(false));
  };

  return { print, isPending };
}
